import { computed, unref, type Ref } from 'vue';
import { dinero, toDecimal } from 'dinero.js';
import { AUD } from 'dinero.js/currencies';
import { useSessionStore, useAppStore } from '@/store';
import { useCustomerPricingSource } from '@/composables/useCustomerPricingSource';
import { priceUnitName, priceUnitSuffix } from '@/composables/usePriceUnitFormatting';
import type { CustomerVisiblePrice } from '@/types';

type OrderItemId = number | string;

export type PriceBreakdownRowData = {
    label: string;
    value: string;
    muted?: boolean;
    /** Shown beneath the label. Only line items and the move's `Rate` row carry one. */
    description?: string | null;
    /** An itemised line, or the `Rate` row its base price line sits behind. Drawn at medium weight. */
    lineItem?: boolean;
};

/**
 * `service_status` is stored with inconsistent casing — `handleBooked` writes the enum key
 * (`BOOKED`), `ConfirmMove` writes the enum description (`Pending`), and older rows carry other
 * variants again. Production currently holds all four of `BOOKED` / `Booked` / `PENDING` /
 * `Pending`, so every comparison here has to be case-insensitive.
 */
/**
 * The icon each service's card already uses (`components/services/**`), shown beside the service's
 * heading in the price breakdown. Keyed by `productable_type`; an unknown type shows no icon.
 */
const SERVICE_ICONS: Record<string, string> = {
    move: 'system-home-filled',
    packing: 'system-box-filled',
    unpacking: 'system-open-box-filled',
    packing_materials: 'system-tape-filled',
    storage: 'garage-home',
    car_transport: 'services-car-transport',
    pet_transport: 'services-pet-transport',
    adhoc_payment: 'system-miscellaneous',
};

export function serviceIcon(productableType: string | null | undefined): string | null {
    return SERVICE_ICONS[(productableType ?? '').toLowerCase()] ?? null;
}

function isServiceStatus(status: string | null | undefined, expected: string): boolean {
    return (status ?? '').toUpperCase() === expected.toUpperCase();
}

/**
 * Whether an order item's deposit has actually been paid.
 *
 * The API's `is_deposit_paid` is authoritative when present: it is resolved server-side with
 * order-level payments spread across the order's services, which is the only way to see the
 * portal's own reservation payment — that payment is recorded with no service link, so the item's
 * `price_owing` never drops when it is charged. The arithmetic below is the fallback for a payload
 * without the field, and it is derived from the payment figures the item does carry rather than
 * from `service_status`: a BOOKED service can still have
 * its deposit outstanding (see `utilities/serviceStatus.ts`), and a CONFIRMED item can be a
 * pending bank transfer or a $0 cardless reservation.
 *
 * `price_owing` is `Move::amount_owing` — total less paid payment items plus processed refunds —
 * rewritten by `Move::updateTotals()` on every payment write, so `total − owing` is the net amount
 * actually received. The two shapes differ: `price_total` / `price_owing` are cknow Money JSON whose
 * `amount` is a *string*; `totals.DEPOSIT` is `PricingMoneyData` with an integer `amount`.
 */
function isDepositPaid(orderItem: any): boolean {
    if (typeof orderItem?.is_deposit_paid === 'boolean') {
        return orderItem.is_deposit_paid;
    }

    if (orderItem?.status !== 'CONFIRMED' || orderItem?.is_pending_bank_deposit) {
        return false;
    }

    const depositCents = Number(orderItem?.totals?.DEPOSIT?.amount ?? 0);
    if (!(depositCents > 0)) {
        return false;
    }

    const totalRaw = orderItem?.price_total?.amount;
    const owingRaw = orderItem?.price_owing?.amount;
    if (totalRaw == null || owingRaw == null) {
        return false;
    }

    const netPaidCents = Number(totalRaw) - Number(owingRaw);

    return Number.isFinite(netPaidCents) && netPaidCents >= depositCents;
}

function depositLabel(orderItem: any): string {
    return isDepositPaid(orderItem) ? 'Deposit (paid)' : 'Deposit (due now)';
}

/**
 * A storage service's start or end date, as the breakdown's "Start date" / "End date" rows show it.
 * Exported so the checkout's storage billing notice names the start date in exactly the same form.
 */
export function formatStorageDate(iso: string | null | undefined, tz?: string | null): string {
    if (!iso) return '-';
    try {
        const date = new Date(iso);
        const formatter = new Intl.DateTimeFormat('en-AU', {
            timeZone: tz ?? undefined,
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
        return formatter.format(date);
    } catch {
        return '-';
    }
}

export function usePriceBreakdown(options?: {
    orderItemIds?: Ref<OrderItemId[]> | OrderItemId[];
    /**
     * Include items that have already been confirmed. Off by default: the checkout flows use this
     * composable to price what is *about to* be paid for, so a confirmed item would inflate the
     * total. The booking summary opts in — post-booking the customer still needs to see what they
     * agreed to, and by then every item is confirmed.
     */
    includeConfirmed?: Ref<boolean> | boolean;
    /**
     * Drop a fixed-priced service's only customer-visible line when it equals the service's total,
     * so a fixed move doesn't read "Fixed price $5,250.00" over "Base Price $5,250.00". The line's
     * description moves up to the total. Off by default; only the two-column Review layout opts in.
     */
    collapseSoleLineItem?: Ref<boolean> | boolean;
    /**
     * State the move's price beside the "Move" header, as every other service states its price:
     * a FIXED move's total instead of a "Fixed price" row, and a VARIABLE move's rate instead of a
     * "Rate" row. Off by default; only the two-column Review layout opts in.
     */
    movePriceInHeader?: Ref<boolean> | boolean;
}) {
    const session = useSessionStore();
    const appStore = useAppStore();
    const pricingSource = useCustomerPricingSource();

    const orderItemIds = computed<OrderItemId[] | undefined>(() => {
        return (options && options.orderItemIds ? unref(options.orderItemIds) : undefined) as OrderItemId[] | undefined;
    });

    const includeConfirmed = computed<boolean>(() => (options ? unref(options.includeConfirmed) === true : false));
    const collapseSoleLineItem = computed<boolean>(() =>
        options ? unref(options.collapseSoleLineItem) === true : false,
    );
    const movePriceInHeader = computed<boolean>(() => (options ? unref(options.movePriceInHeader) === true : false));

    const parseMoneyStringToCents = (formatted: string | null | undefined): number => {
        if (!formatted) return 0;
        const cleaned = formatted.replace(/[$,\s]/g, '');
        const value = parseFloat(cleaned);
        return Number.isFinite(value) ? Math.round(value * 100) : 0;
    };

    /**
     * The one customer-visible line that merely repeats the service's total, when the caller has
     * asked for such a line to be dropped. Compared in cents, because both figures arrive as
     * formatted strings from the same API money formatter.
     */
    const soleLineEqualToTotal = (lines: CustomerVisiblePrice[], total: string | null | undefined) => {
        if (!collapseSoleLineItem.value || lines.length !== 1 || !total) {
            return null;
        }
        const totalCents = parseMoneyStringToCents(total);

        return totalCents > 0 && parseMoneyStringToCents(lines[0].value) === totalCents ? lines[0] : null;
    };

    /** Fixed suffixes for a line's `CostItemUnit`, in the "/ unit" form the service header uses. */
    const LINE_UNIT_SUFFIXES: Record<string, string> = {
        PER_HOUR: '/ hour',
        PER_HALF_HOUR: '/ 30 min',
        PER_ITEM: '/ item',
        PER_CUBIC_METER: '/ m³',
        PER_SQUARE_METER: '/ m²',
        PER_DAY: '/ day',
        PER_PERSON: '/ person',
        PER_KM: '/ km',
        PER_CUBIC_METER_PER_FLIGHT: '/ m³ per flight',
        PER_CUBIC_METER_PER_25_METERS: '/ m³ per 25 m',
    };

    /**
     * A rate-quoted service's line stated as a rate, or null to keep its total. A per-30-minute
     * line follows the tenant's per-hour setting with the API's `unit_price_per_hour`, as the
     * header uses its `price_per_hour`, rather than doubling a figure already rounded ex tax. An
     * item line reads "/ item" on every service, a per-box one included, as the CRM and the
     * payment breakdown word it (CU-14zcqntnmd0).
     */
    const lineAsRate = (price: CustomerVisiblePrice): string | null => {
        if (!price.unit || !price.unit_price || !LINE_UNIT_SUFFIXES[price.unit]) {
            return null;
        }

        if (price.unit === 'PER_HALF_HOUR' && appStore.tenantPerHourPricing && price.unit_price_per_hour) {
            return `${price.unit_price_per_hour} / hour`;
        }

        return `${price.unit_price} ${LINE_UNIT_SUFFIXES[price.unit]}`;
    };

    /**
     * The breakdown's items, and beside them each item's shown total in cents: the one figure the
     * card states as that service's price. Null for a service quoted as a rate, for storage (billed
     * per period) and for a fixed service with no total. A cancelled or declined service has no
     * entry: the card still lists it, but the customer no longer owes it. Built in the same pass as the header, so
     * the Order total can never drift from the figures the customer is adding up.
     */
    const pricedItems = computed(() => {
        const shownTotals: Array<number | null> = [];
        const items: Array<{
            label: string;
            icon: string | null;
            unitCost: string | null;
            /** Shown beneath the header: a dropped sole line's description (see `collapseSoleLineItem`). */
            unitCostDescription?: string | null;
            rows: PriceBreakdownRowData[];
            orderItemId: OrderItemId;
            productableType: string | null;
        }> = [];

        const orderItems = (session.order as any)?.items ?? [];

        // A move confirmed online sits at `Pending` until the operator books it, so it is still
        // "confirmable" and belongs in the checkout total. Once booked it is not, and only the
        // booking summary (which opts in) should still show it.
        let confirmableItems = orderItems.filter(
            (item: any) =>
                includeConfirmed.value ||
                item?.status !== 'CONFIRMED' ||
                isServiceStatus(item?.service_status, 'PENDING'),
        );

        const ids = unref(orderItemIds);
        if (ids && ids.length) {
            const idSet = new Set(ids);
            confirmableItems = confirmableItems.filter((i: any) => idSet.has(i.id));
        }

        // Helpers
        const deriveUnit = (product: any): string | null => {
            return priceUnitName(product?.price_unit_formatted);
        };

        confirmableItems.forEach((orderItem: any) => {
            const label = orderItem.service_label ?? orderItem.service_type ?? 'Service';
            const icon = serviceIcon(orderItem.productable_type);
            const countsTowardOrderTotal = !['CANCELLED', 'DECLINED'].some((status) =>
                isServiceStatus(orderItem.service_status, status),
            );
            let unitCost: string | null = null;
            let isVariable: boolean;
            let isMove = false;
            // A completed service has its final quantity captured, so its balance is real; until then a
            // VARIABLE service's balance is unknown. Applies to the Move too: the confirmation page is
            // revisited after the job completes, and "To be calculated after service complete" must not
            // outlive the completion it refers to.
            const isComplete = isServiceStatus(orderItem.service_status, 'COMPLETED');
            const identity = {
                orderItemId: orderItem.id as OrderItemId,
                productableType: ((orderItem.productable_type ?? null) as string | null)?.toLowerCase() ?? null,
            };
            let unitCostDescription: string | null = null;
            let shownTotal: string | null;

            // STORAGE handling
            const isStorage = ((orderItem.productable_type ?? '') as string).toLowerCase() === 'storage';
            if (isStorage) {
                const product = orderItem.product ?? {};
                const unit = deriveUnit(product);
                const storagePrice = product?.price?.formatted;
                if (storagePrice && unit) {
                    unitCost = `${storagePrice} / ${unit}`;
                } else if (storagePrice) {
                    unitCost = storagePrice;
                }

                const start = formatStorageDate(product?.start_date, product?.timezone?.timezone);
                const end = formatStorageDate(product?.end_date, product?.timezone?.timezone);

                const rows: PriceBreakdownRowData[] = [
                    { label: 'Start date', value: start },
                    { label: 'End date', value: end },
                ];
                // Storage that has already started is charged up front at confirmation for the
                // cycles elapsed (CU-86d3nb7d5). The API sends that figure only while it applies.
                if (orderItem.status !== 'CONFIRMED' && product?.upfront_amount?.formatted) {
                    rows.push({ label: 'Charged today', value: product.upfront_amount.formatted });
                }

                items.push({ label, icon, unitCost, rows, ...identity });
                if (countsTowardOrderTotal) {
                    shownTotals.push(null);
                }
                return; // proceed next item
            }

            if ((orderItem.productable_type ?? '').toLowerCase() === 'move') {
                // Pricing fields resolve from the booked job once one is active, so
                // post-booking operator edits reach the customer; match pre-booking.
                const pricingType = pricingSource.pricingType.value;
                isVariable = pricingType === 'VARIABLE';
                isMove = true;
                // A fixed move's price is its all-in total, shown as "Fixed price" or beside the header.
                shownTotal = isVariable ? null : (orderItem.totals?.TOTAL?.formatted ?? null);
                // A move's rate is an itemised "Rate" / "Fixed price" row, not a bare figure
                // beside the header — see the row block below. `unitCost` stays null so the
                // template drops the header value, unless `movePriceInHeader` puts the move's
                // total or rate there.
            } else {
                const product = orderItem.product ?? {};
                const rawBasePrice: string | null =
                    product?.price?.formatted ?? orderItem.totals?.TOTAL?.formatted ?? null;
                const basePrice: string | null = rawBasePrice;
                const priceUnit: string | null = product?.price_unit ?? null;
                const priceUnitFormatted: any = product?.price_unit_formatted ?? null;
                // Backend exposes a doubled `price_per_hour` for variable per-30-min
                // services when the tenant's per-hour pricing setting is on. Display only.
                const pricePerHour: string | null = product?.price_per_hour?.formatted ?? null;

                // Determine variable status for non-move items. `isVariable` only decides whether
                // the balance is still unknown, so it has to fall away once the service completes
                // — by then the final quantity has been captured and the balance is real. Keying
                // it on price_type alone left "To be calculated after service complete" on screen
                // permanently, with the actual amount hidden behind it.
                const productPriceType: string | null = product?.price_type ?? null;
                const depositType: string | null = product?.deposit_type ?? null;
                const quantity: number | null = product?.quantity ?? null;
                isVariable =
                    !isComplete && (productPriceType === 'VARIABLE' || depositType === 'METERED' || !!quantity);

                // The "/ box", "/ hour" suffix only belongs on a quote that *is* a rate. A fixed
                // quote is one number, and a `price_unit` left behind from when it was variable
                // advertised "$669.30 / box" for a service the customer owes $669.30 for, full stop
                // (CU-86d45pvyd, James' review). A Variable service stops being a rate once its
                // quantity is stipulated, which is the API's `is_rate_quote` (CU-14zcqntnmd0).
                const quotesARate = orderItem.is_rate_quote === true;
                // Its price is still the rate then, so the header states the total it settled on.
                const settledFromRate = productPriceType === 'VARIABLE' && !quotesARate;
                const headerPrice = settledFromRate ? (orderItem.totals?.TOTAL?.formatted ?? basePrice) : basePrice;
                // A settled total means a known balance too, including a service taken back to
                // Booked with its quantity kept, so the Remaining row agrees with the header.
                if (settledFromRate) {
                    isVariable = false;
                }
                // The card's figure for this service, which the Order total adds up.
                shownTotal = quotesARate ? null : headerPrice;

                if (pricePerHour && quotesARate) {
                    unitCost = `${pricePerHour} / hour`;
                } else if (headerPrice) {
                    const suffix = quotesARate ? priceUnitSuffix(priceUnit ?? undefined, priceUnitFormatted) : null;
                    unitCost = suffix ? `${headerPrice} ${suffix}` : headerPrice;
                } else {
                    unitCost = null;
                }
            }

            // BALANCE is `amount_total - deposit` with no floor, so a service that finishes under
            // the deposited quantity produces a negative. The customer is still charged
            // max(total, deposit) — see PackingProductableTrait::getAmountOwingAttribute — so
            // nothing is actually owed, and surfacing a negative here would read as a refund.
            const balanceAmount = Number(orderItem.totals?.BALANCE?.amount ?? 0);
            const balanceUnknown = isVariable && !isComplete;
            const remaining: string | null =
                balanceUnknown ? null
                : orderItem.totals?.BALANCE?.formatted && balanceAmount >= 0 ? orderItem.totals.BALANCE.formatted
                : null;
            // For interstate moves not within a week, show note next to label
            const remainingLabelNote: string | null =
                isMove && !session.isLocal && session.getSelectedMatch?.within_week != 1 ?
                    (() => {
                        const days = appStore.tenantFinalPaymentDays;
                        if (days > 0) return ` (${days} days before move)`;
                        if (days < 0) return ` (${Math.abs(days)} days after move)`;
                        return ' (on move day)';
                    })()
                :   null;
            const remainingNote = balanceUnknown ? 'To be calculated after service complete' : null;

            const rows: PriceBreakdownRowData[] = [];

            // For Move items, add pricing breakdown sections
            if (isMove) {
                const selectedMatch = session.getSelectedMatch;
                const pricingType = pricingSource.pricingType.value;

                // The agreed rate, stated first — a customer reading the card after booking needs
                // to see what they are charged per unit, not just the totals derived from it.
                if (pricingType === 'VARIABLE') {
                    // `price_per_unit` is the job's rate once a live job exists and the match's
                    // beforehand — the same source `Match.vue` reads. The API has already doubled
                    // it to an hourly figure when the tenant has `per_hour_pricing` on (see
                    // `DenormalizesPricePerUnitOutput`), so only the suffix varies here. There is
                    // no `price_per_hour` to branch on: both booking resources emit the key, but
                    // no such column exists on `move_matches` or `move_jobs`, so it is always null
                    // — which is how an hourly figure came to be labelled "/ 30 mins".
                    const rate = pricingSource.source.value?.price_per_unit?.formatted;
                    if (rate && movePriceInHeader.value) {
                        unitCost = `${rate} ${appStore.tenantPerHourPricing ? '/ hour' : '/ 30 mins'}`;
                        unitCostDescription = pricingSource.rateDescription.value ?? null;
                    } else if (rate) {
                        rows.push({
                            label: 'Rate',
                            value: `${rate} ${appStore.tenantPerHourPricing ? '/ hour' : '/ 30 mins'}`,
                            // The base price line's description, from the same source as the rate.
                            description: pricingSource.rateDescription.value ?? undefined,
                            lineItem: true,
                        });
                    }
                } else if (orderItem.totals?.TOTAL?.formatted) {
                    // FIXED moves are a single all-in price: every line (itemised visible lines and
                    // hidden "Other fees") is folded in, so it equals the order total.
                    const soleLine = soleLineEqualToTotal(
                        pricingSource.customerVisiblePrices.value,
                        orderItem.totals.TOTAL.formatted,
                    );
                    // A dropped sole line's description goes under the header, never on the
                    // "Fixed price" row, which carries no description (show-line-item-descriptions spec)
                    unitCostDescription = soleLine?.description ?? null;
                    if (movePriceInHeader.value) {
                        unitCost = orderItem.totals.TOTAL.formatted;
                    } else {
                        rows.push({ label: 'Fixed price', value: orderItem.totals.TOTAL.formatted });
                    }
                }

                // Keep the fee's label, amount and time on the same live job or selected match.
                if (pricingType === 'VARIABLE') {
                    const feeSource = pricingSource.source.value;
                    const callOutFeeAmount = Number(feeSource?.call_out_fee?.amount ?? 0);
                    if (callOutFeeAmount > 0) {
                        const callOutFeeValue =
                            appStore.tenantCallOutFeeAsTime && feeSource?.call_out_fee_time ?
                                feeSource.call_out_fee_time
                            :   feeSource?.call_out_fee?.formatted || '-';
                        rows.push({ label: feeSource?.call_out_fee_label || 'Call-out fee', value: callOutFeeValue });
                    }
                }

                // Visible (non-hidden) lines are itemised underneath the "Move" header for both
                // pricing types. On FIXED the "Fixed price" row (or header total) already equals the full total
                // (all values fold in), so these rows are an "of which" detail; the hidden fees are
                // absorbed into it and never itemised (only VARIABLE shows "Other fees" below).
                const customerVisiblePrices: CustomerVisiblePrice[] = pricingSource.customerVisiblePrices.value;
                const droppedMoveLine =
                    pricingType === 'VARIABLE' ? null : (
                        soleLineEqualToTotal(customerVisiblePrices, orderItem.totals?.TOTAL?.formatted)
                    );
                for (const price of droppedMoveLine ? [] : customerVisiblePrices) {
                    rows.push({
                        label: price.label,
                        value: price.value,
                        description: price.description ?? undefined,
                        lineItem: true,
                    });
                }

                // Only VARIABLE moves surface "Other fees" as its own line; FIXED folds it into Move above.
                const otherFees = pricingSource.otherFees.value;
                if (pricingType === 'VARIABLE' && Number(otherFees?.amount ?? 0) > 0 && otherFees?.formatted) {
                    rows.push({
                        label: pricingSource.otherFeesLabel.value,
                        value: otherFees.formatted,
                    });
                }

                // Deposit
                rows.push({
                    label: depositLabel(orderItem),
                    value: orderItem.totals?.DEPOSIT?.formatted || '-',
                });

                if (pricingType === 'VARIABLE') {
                    rows.push({
                        label: 'Minimum hours',
                        value: Number(selectedMatch?.min_time ?? appStore.tenantMinHours).toFixed(0) + ' hours',
                    });
                }

                // Final payment (renamed from Remaining)
                if (appStore.tenantAutoCreateFinal) {
                    if (remainingNote) {
                        const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                        rows.push({ label: lbl, value: remainingNote, muted: true });
                    } else {
                        const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                        rows.push({ label: lbl, value: (remaining ?? '-') as string });
                    }
                }
            } else {
                // Itemised lines the operator marked customer-visible, above the deposit — the same
                // "of which" detail a Move shows under its own header. Without these a packing or
                // unpacking service reached the customer as one figure with a deposit under it, and
                // every extra line on it (a Tea Chest, an access fee) was invisible to the person
                // being asked to pay for it (CU-86d45pvyd, James' review).
                //
                // Rendered for both price types: on a rate quote they are the rates being charged,
                // and on a fixed one they are what the header total is made of.
                const serviceLines = (orderItem.customer_visible_prices ?? []) as CustomerVisiblePrice[];
                // The API's `is_rate_quote` (`OrderItemPriceSyncer::isUnstipulatedRateQuote`): a
                // Variable service not yet completed and, for packing or unpacking, with no
                // stipulated quantity. Price type alone would state units on a completed service.
                const statesRates = orderItem.is_rate_quote === true;
                // A rate line is a unit price, not the total, so only a settled service can drop one.
                const droppedServiceLine =
                    statesRates ? null : soleLineEqualToTotal(serviceLines, orderItem.totals?.TOTAL?.formatted);
                unitCostDescription = droppedServiceLine?.description ?? null;
                // Packing materials have no Base price: each line is one material's rate.
                const everyLineIsARate = orderItem.productable_type === 'packing_materials';
                for (const price of droppedServiceLine ? [] : serviceLines) {
                    // On packing and unpacking only the Base price line is the rate. Every other
                    // line (a Tea Chest counted at 5, an access fee) is a charge the operator
                    // counted, so it keeps its total (CU-14zcqntnmd0).
                    const isRate = statesRates && (everyLineIsARate || price.is_base_price === true);
                    rows.push({
                        label: price.label,
                        value: (isRate ? lineAsRate(price) : null) ?? price.value,
                        description: price.description ?? undefined,
                        lineItem: true,
                    });
                }

                // Deposit row
                rows.push({
                    label: depositLabel(orderItem),
                    value: orderItem.totals?.DEPOSIT?.formatted || '-',
                });
                // Remaining row
                if (remainingNote) {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: remainingNote, muted: true });
                } else {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: (remaining ?? '-') as string });
                }
            }

            items.push({
                label,
                icon,
                unitCost,
                ...(unitCostDescription ? { unitCostDescription } : {}),
                rows,
                ...identity,
            });
            if (countsTowardOrderTotal) {
                shownTotals.push(shownTotal ? parseMoneyStringToCents(shownTotal) : null);
            }
        });

        return { items, shownTotals };
    });

    const breakdownItems = computed(() => pricedItems.value.items);

    const formatCents = (cents: number): string =>
        new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
            parseFloat(toDecimal(dinero({ amount: cents, currency: AUD }))),
        );

    /**
     * The whole order's price: the sum of the service totals the card shows, so it follows the
     * card's GST mode as they do. Null, so no row is shown, unless every listed service shows a
     * single total, Fixed or a Variable one whose quantity is settled: a rate or storage has no
     * single figure to add (James Morrell, 23 Oct 2026).
     * Cancelled and declined services are left out, and an order with nothing left to count shows
     * no row.
     */
    const orderTotal = computed<string | null>(() => {
        const { shownTotals } = pricedItems.value;
        if (shownTotals.length === 0 || shownTotals.some((cents) => cents === null)) {
            return null;
        }

        return formatCents(shownTotals.reduce<number>((sum, cents) => sum + (cents ?? 0), 0));
    });

    const displayPricesExTax = computed(
        () => (appStore.tenant as any)?.company?.settings?.display_prices_ex_tax ?? false,
    );

    const dueNowTotal = computed(() => {
        const computeDueNow = (ids?: Array<number | string>) => {
            const orderItems = (session.order as any)?.items ?? [];
            const idSet = ids && ids.length > 0 ? new Set(ids) : null;
            // Sum the same DEPOSIT.formatted values shown in the deposit rows. The backend already
            // applies any ex-tax / region adjustment to `.formatted` (PricingMoneyData), so the total
            // stays consistent with the displayed rows without re-deriving the tax rate here.
            const cents = orderItems
                .filter((item: any) => item?.status !== 'CONFIRMED')
                .filter((item: any) => (idSet ? idSet.has(item.id) : true))
                .reduce((acc: number, item: any) => {
                    // Storage is billed from its start date, so only the up-front charge for
                    // storage that has already started is due now (CU-86d3nb7d5).
                    if (((item?.productable_type ?? '') as string).toLowerCase() === 'storage') {
                        return acc + parseMoneyStringToCents(item?.product?.upfront_amount?.formatted);
                    }
                    return acc + parseMoneyStringToCents(item?.totals?.DEPOSIT?.formatted);
                }, 0);

            return formatCents(cents);
        };

        const ids = orderItemIds.value as Array<number | string> | undefined;
        return computeDueNow(ids);
    });

    return {
        breakdownItems,
        dueNowTotal,
        orderTotal,
        displayPricesExTax,
    };
}
